import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { User, Clock, Building2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";

const memberTypes = [
    {
        type: "regular",
        icon: User,
        title: "Regular Members",
        subtitle: "City Employees & Community Members",
        description:
            "Join a network of dedicated city employees and community members. Access exclusive events, connect with colleagues, and contribute to civic initiatives.",
        features: [
            "Access member directory",
            "Join topic channels",
            "Attend community events",
            "Volunteer opportunities",
        ],
        gradient: "from-accent to-primary",
        path: "/dashboard",
    },
    {
        type: "retired",
        icon: Clock,
        title: "Retired Professionals",
        subtitle: "Share Your Expertise",
        description:
            "Stay connected to the community you've served. Share decades of experience, mentor new professionals, and continue making an impact in retirement.",
        features: [
            "Mentorship programs",
            "SOP contributions",
            "Consulting opportunities",
            "Knowledge marketplace",
        ],
        gradient: "from-primary to-accent",
        path: "/retired-dashboard",
    },
    {
        type: "sponsor",
        icon: Building2,
        title: "Sponsors",
        subtitle: "Support Local Initiatives",
        description:
            "Connect your organization with meaningful civic initiatives. Gain visibility, support community events, and build lasting relationships with local government.",
        features: [
            "Sponsorship packages",
            "Event partnerships",
            "Analytics dashboard",
            "Brand visibility",
        ],
        gradient: "from-accent to-primary",
        path: "/sponsor-analytics",
    },
];

export const CommunitySection = () => {
    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: "-100px" });
    const navigate = useNavigate();

    return (
        <section id="community" className="section-padding bg-muted/30 relative overflow-hidden">
            <div className="absolute inset-0 mesh-gradient opacity-30" />

            <div className="container mx-auto relative z-10" ref={ref}>
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.6 }}
                    className="text-center max-w-3xl mx-auto mb-10"
                >
                    <span className="text-accent font-semibold text-sm uppercase tracking-wider mb-4 block">
                        Community
                    </span>
                    <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
                        Find Your Place in the{" "}
                        <span className="gradient-text">Community</span>
                    </h2>
                    <p className="text-muted-foreground text-lg">
                        Whether you're an active employee, a retired professional, or an organization looking to make an impact, there's a place for you here.
                    </p>
                </motion.div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
                    {memberTypes.map((member, index) => (
                        <motion.div
                            key={member.type}
                            initial={{ opacity: 0, y: 40 }}
                            animate={isInView ? { opacity: 1, y: 0 } : {}}
                            transition={{ duration: 0.6, delay: index * 0.15 }}
                            className="group"
                        >
                            <div className="glass-card-strong rounded-2xl md:rounded-3xl p-5 md:p-6 h-full flex flex-col hover-lift transition-all duration-500 hover:border-accent/40">
                                <div
                                    className={`w-12 h-12 md:w-14 md:h-14 rounded-xl bg-gradient-to-br ${member.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                                >
                                    <member.icon className="h-6 w-6 md:h-7 md:w-7 text-accent-foreground" />
                                </div>
                                <h3 className="font-display font-bold text-lg md:text-xl mb-1 text-foreground">
                                    {member.title}
                                </h3>
                                <p className="text-accent font-medium text-xs md:text-sm mb-3">
                                    {member.subtitle}
                                </p>
                                <p className="text-muted-foreground leading-relaxed mb-4 flex-grow text-sm">
                                    {member.description}
                                </p>
                                <ul className="space-y-1.5 mb-4">
                                    {member.features.map((feature) => (
                                        <li key={feature} className="flex items-center gap-2 text-xs md:text-sm text-foreground">
                                            <div className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                                <Button
                                    className="w-full group/btn bg-primary hover:bg-primary/90 text-sm"
                                    size="sm"
                                >
                                    <span className="truncate">Explore {member.title}</span>
                                </Button>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
